import {
  DriverWorkShift,
  OperationPlan,
  Tab1,
  Tab2,
  Tab3,
  Tabs,
} from "@/models/operationPlans";
import { getJSTCurrentTime } from "@/util/datetime";

export const OperationPlansConstansts = {
  OperationPlan: "車両稼働予定",
  OfficeName: "会社／営業所",
  Year: "年月",
  Month: "月",
  OneToTen: "1日-10日",
  ElevenToTwenty: "11日-20日",
  TwentyOneToEndOfMonth: "21日-月末",
  OperationPlanEditing: "車両稼働予定編集",
  BelongingOfficeName: "所属営業所名",
  RadioNumber: "無線番号",
  OperationPlanDate: "運行計画日",
  TakeABreak: "休車",
  OperationPlanWeekdaysHolidays: "運行計画：平日・土日",
  WorkShiftTable: "勤務シフト(表)",
  WorkShiftTableStartTime: "勤務シフト(表)：開始時刻",
  WorkShiftTableEndTime: "勤務シフト(表)：終了時刻",
  WorkShiftBack: "勤務シフト(裏)",
  WorkShiftBackStartTime: "勤務シフト(裏)：開始時刻",
  WorkShiftBackEndTime: "勤務シフト(裏)：終了時刻",
  TargetDate: "対象年月",
  SameTimeError:
    "開始時刻と同じ時間が設定されています。時間を見直してください。",
} as const;

export const sticky = {
  textAlign: "center",
  position: "sticky",
  backgroundColor: "inherit",
  zIndex: 999,
} as const;

export const hover = {
  backgroundColor: "white",
  "&:hover": {
    backgroundColor: "rgb(245, 245, 245)",
  },
};

export const getLabelOf = (plan: OperationPlan, key: "front" | "back") => {
  if (["0", "1"].includes(plan.stopCarType ?? "")) {
    return undefined;
  }

  if (plan[`${key}DriverWorkShifts`]?.shortName != null) {
    return plan[`${key}DriverWorkShifts`]?.shortName;
  }

  return `${plan[`${key}DriverWorkStartHours`] ?? ""}:${
    plan[`${key}DriverWorkStartMinutes`]?.toString().padStart(2, "0") ?? ""
  }-${plan[`${key}DriverWorkEndHours`] ?? ""}:${
    plan[`${key}DriverWorkEndMinutes`]?.toString().padStart(2, "0") ?? ""
  }`;
};

export const labelOf = (plan: OperationPlan) => {
  const frontLabel = getLabelOf(plan, "front");
  const backLabel = getLabelOf(plan, "back");

  if (frontLabel == null && backLabel == null) {
    return "休車";
  }

  if (frontLabel == null) {
    return backLabel;
  }

  if (backLabel == null) {
    return frontLabel;
  }

  return `${frontLabel} / ${backLabel}`;
};

const Days = ["日", "月", "火", "水", "木", "金", "土"] as const;
export const getDateRange = (activeTab: Tabs, targetMonth: string) => {
  const baseMonth = new Date(targetMonth);

  const from =
    activeTab === Tab1
      ? new Date(targetMonth)
      : activeTab === Tab2
      ? new Date(new Date(baseMonth).setDate(11))
      : new Date(new Date(baseMonth).setDate(21));

  const to =
    activeTab === Tab1
      ? new Date(new Date(targetMonth).setDate(11))
      : activeTab === Tab2
      ? new Date(new Date(baseMonth).setDate(21))
      : new Date(new Date(baseMonth).setMonth(baseMonth.getMonth() + 1));

  const dateLabels = [...Array(11)]
    .map((_, i) => {
      const d = new Date(new Date(from).setDate(from.getDate() + i));
      if (d >= to) {
        return undefined;
      }

      return `${d.getMonth() + 1}/${d.getDate()}`;
    })
    .filter((v) => v != null) as string[];

  const dateTypeLabels = [...Array(11)]
    .map((_, i) => {
      const d = new Date(new Date(from).setDate(from.getDate() + i));
      if (d >= to) {
        return undefined;
      }

      return Days[d.getDay()];
    })
    .filter((v) => v != null) as (typeof Days)[number][];

  return { from, to, dateLabels, dateTypeLabels };
};

export const getAllDateAttributes = (targetYm: string) => {
  const baseMonth = new Date(targetYm);

  const from = new Date(targetYm);
  const to = (() => {
    const toDate = new Date(baseMonth);
    toDate.setMonth(baseMonth.getMonth() + 1);
    return toDate;
  })();

  const days = [...Array(31)]
    .map((_, i) => new Date(new Date(from).setDate(from.getDate() + i)))
    .filter((d) => d < to);

  const dateLabels = days.map((d) => `${d.getMonth() + 1}/${d.getDate()}`);
  const dateTypeLabels = days.map((d) => Days[d.getDay()]);

  return { from, to, dateLabels, dateTypeLabels };
};

export const getTabsAttributes = (targetMonth: string) => {
  return {
    Tab1: getDateRange(Tab1, targetMonth),
    Tab2: getDateRange(Tab2, targetMonth),
    Tab3: getDateRange(Tab3, targetMonth),
  };
};

export const editablePlan = (plan: OperationPlan) => {
  const targetDate = new Date(plan.targetDate.slice(0, 10));
  const now = new Date(getJSTCurrentTime("date"));

  return targetDate >= now;
};

export const downtimeType: ["―", "休車", "休車（請求なし）"] = [
  "―",
  "休車",
  "休車（請求なし）",
];
export const downtimeTypeForEditUser: ["―", "休車"] = ["―", "休車"];

export const workingMins: ["00", "30"] = ["00", "30"];
export const holidayFlags: ["平日", "土日"] = ["平日", "土日"];
export const workingHours: [
  "00",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23"
] = [
  "00",
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "19",
  "20",
  "21",
  "22",
  "23",
];

export const getBinCode = (
  plan: OperationPlan,
  sec: "front" | "back",
  sec2: "Start" | "End"
) => {
  return `${
    plan[`${sec}DriverWork${sec2}Hours`]?.toString().padStart(2, "0") ?? ""
  }:${
    plan[`${sec}DriverWork${sec2}Minutes`]?.toString().padStart(2, "0") ?? ""
  }`;
};

export const BINS = [...Array(48)].map((_, i) => {
  const f = i * 0.5;
  const t = i < 47 ? (i + 1) * 0.5 : 0;

  return {
    bin: i,
    from: `${Math.floor(f).toString().padStart(2, "0")}:${
      Number.isInteger(f) ? "00" : "30"
    }`,
    to: `${Math.floor(t).toString().padStart(2, "0")}:${
      Number.isInteger(t) ? "00" : "30"
    }`,
  };
});

export const findShift = (
  shifts: DriverWorkShift[],
  plan: OperationPlan,
  sec: "front" | "back"
) => {
  const from = getBinCode(plan, sec, "Start");
  const to = getBinCode(plan, sec, "End");
  const key = plan.isHoliday ? "holiday" : "weekday";

  return shifts.find((shift) => {
    const fromBin = BINS[shift[`${key}OutgoingBin`]];
    const toBin = BINS[shift[`${key}IncomingBin`]];

    return (
      !shift[`${key}IsDayOff`] && from === fromBin?.from && to === toBin?.to
    );
  });
};

export const shiftToTime = (isHoliday: boolean, shift?: DriverWorkShift) => {
  const empty = {
    fromH: undefined,
    fromM: undefined,
    toH: undefined,
    toM: undefined,
  };

  // 休日+休日休みシフト or 平日+平日休みシフトの場合は時間を空で返す
  if (
    shift == null ||
    (isHoliday && shift.holidayIsDayOff) ||
    (!isHoliday && shift.weekdayIsDayOff)
  ) {
    return empty;
  }

  const key = isHoliday ? "holiday" : "weekday";
  const fromBin = BINS[shift[`${key}OutgoingBin`]];
  const toBin = BINS[shift[`${key}IncomingBin`]];
  if (fromBin == null || toBin == null) {
    return empty;
  }

  const from = fromBin.from.split(":").map((s) => Number(s));
  const to = toBin.to.split(":").map((s) => Number(s));

  return {
    fromH: from[0],
    fromM: from[1],
    toH: to[0],
    toM: to[1],
  };
};

export const getTimes = (plan: OperationPlan, key: "front" | "back") => {
  const shift = plan[`${key}DriverWorkShifts`];
  if (shift == null) {
    return {
      fromH: plan[`${key}DriverWorkStartHours`],
      fromM: plan[`${key}DriverWorkStartMinutes`],
      toH: plan[`${key}DriverWorkEndHours`],
      toM: plan[`${key}DriverWorkEndMinutes`],
    };
  }

  return shiftToTime(plan.isHoliday, shift);
};

export const duplicateShifts = (plan: OperationPlan) => {
  if (["0", "1"].includes(plan.stopCarType ?? "")) {
    return false;
  }

  const frontShift = plan.frontDriverWorkShifts;
  const backShift = plan.backDriverWorkShifts;
  // 休日において、表・裏のいずれかが休み扱いである場合は、重複なしと判定する
  if (
    plan.isHoliday &&
    (!!frontShift?.holidayIsDayOff || backShift?.holidayIsDayOff)
  ) {
    return false;
  }

  // 平日において、表・裏のいずれかが休み扱いである場合は、重複なしと判定する
  if (
    !plan.isHoliday &&
    (!!frontShift?.weekdayIsDayOff || backShift?.weekdayIsDayOff)
  ) {
    return false;
  }

  const frontFrom =
    BINS.find((bin) => bin.from === getBinCode(plan, "front", "Start"))?.bin ??
    0;

  let frontTo =
    BINS.find((bin) => bin.to === getBinCode(plan, "front", "End"))?.bin ?? 0;
  frontTo = frontTo < frontFrom ? frontTo + 47 : frontTo;

  const backFrom =
    BINS.find((bin) => bin.from === getBinCode(plan, "back", "Start"))?.bin ??
    0;

  let backTo =
    BINS.find((bin) => bin.to === getBinCode(plan, "back", "End"))?.bin ?? 0;
  backTo = backTo < backFrom ? backTo + 47 : backTo;

  const frontBins = [...Array(Math.abs(frontFrom - frontTo) + 1)].map(
    (_, i) => i + Math.min(frontFrom, frontTo)
  );
  const backBins = [...Array(Math.abs(backFrom - backTo) + 1)].map(
    (_, i) => i + Math.min(backFrom, backTo)
  );

  return [...frontBins, ...backBins].some(
    (bin) => frontBins.includes(bin) && backBins.includes(bin)
  );
};

// 開始終了時刻が同一かどうか判定する
export const duplicateTime = (plan: OperationPlan, key: "front" | "back") => {
  const startH = plan[`${key}DriverWorkStartHours`];
  const startM = plan[`${key}DriverWorkStartMinutes`];
  const endH = plan[`${key}DriverWorkEndHours`];
  const endM = plan[`${key}DriverWorkEndMinutes`];

  return (
    startH != null &&
    startM != null &&
    endH != null &&
    endM != null &&
    startH === endH &&
    startM === endM
  );
};

export const isEqualPlan = (p1?: OperationPlan, p2?: OperationPlan) => {
  return (
    nullLazyEqual(p1?.isHoliday, p2?.isHoliday) &&
    nullLazyEqual(p1?.stopCarType, p2?.stopCarType) &&
    nullLazyEqual(p1?.frontDriverWorkShiftId, p2?.frontDriverWorkShiftId) &&
    nullLazyEqual(
      p1?.frontDriverWorkStartHours,
      p2?.frontDriverWorkStartHours
    ) &&
    nullLazyEqual(
      p1?.frontDriverWorkStartMinutes,
      p2?.frontDriverWorkStartMinutes
    ) &&
    nullLazyEqual(p1?.frontDriverWorkEndHours, p2?.frontDriverWorkEndHours) &&
    nullLazyEqual(
      p1?.frontDriverWorkEndMinutes,
      p2?.frontDriverWorkEndMinutes
    ) &&
    nullLazyEqual(p1?.backDriverWorkShiftId, p2?.backDriverWorkShiftId) &&
    nullLazyEqual(p1?.backDriverWorkStartHours, p2?.backDriverWorkStartHours) &&
    nullLazyEqual(
      p1?.backDriverWorkStartMinutes,
      p2?.backDriverWorkStartMinutes
    ) &&
    nullLazyEqual(p1?.backDriverWorkEndHours, p2?.backDriverWorkEndHours) &&
    nullLazyEqual(p1?.backDriverWorkEndMinutes, p2?.backDriverWorkEndMinutes)
  );
};

// null と undefined の比較は true とするカスタム比較
const nullLazyEqual = <T>(v1?: T, v2?: T) => {
  return (v1 == null && v2 == null) || v1 === v2;
};
